(() => {
  const config = JSON.parse(document.getElementById('PhotoCatalogConfig')?.textContent || '{}');
  const review = config.review === true && new URLSearchParams(location.search).get('catalog') !== 'live';
  const root = config.root || '/';
  const id = value => String(value || '').split('/').pop();
  const productCache = new Map();
  let catalogPromise;
  const product = async (handle, expectedId, refresh = false) => {
    if (!/^[a-z0-9-]+$/.test(handle || '')) throw new Error('Invalid photograph');
    const key = handle + ':' + id(expectedId);
    if (refresh) productCache.delete(key);
    if (!productCache.has(key)) productCache.set(key, (async () => {
      const response = await fetch(root + 'products/' + encodeURIComponent(handle) + '.js', {credentials:'same-origin',cache:'no-store'});
      if (!response.ok) throw new Error('Photograph unavailable');
      const data = await response.json();
      if (id(data.id) !== id(expectedId) || !Array.isArray(data.variants)) throw new Error('Photograph changed');
      return data;
    })());
    try { return await productCache.get(key); }
    catch (error) { productCache.delete(key); throw error; }
  };
  const currentProducts = async () => {
    const products = new Map();
    let page = 1;
    while (page) {
      const url = new URL(root,location.origin);
      url.searchParams.set('section_id','photo-runtime-catalog');
      url.searchParams.set('page',String(page));
      const response = await fetch(url,{credentials:'same-origin',cache:'no-store'});
      if (!response.ok) throw new Error('Catalog unavailable');
      const html = new DOMParser().parseFromString(await response.text(),'text/html');
      const node = html.querySelector('[data-photo-runtime]');
      if (!node) throw new Error('Catalog unavailable');
      const data = JSON.parse(node.textContent);
      for (const item of data.products) products.set(id(item.id),item);
      const next = Number(data.next);
      if (next && next !== page + 1) throw new Error('Catalog pagination changed');
      page = next;
      if (page > 20) throw new Error('Catalog pagination exceeded');
    }
    return products;
  };
  const load = async source => {
    if (review) {
      const response = await fetch(source,{credentials:'same-origin'});
      if (!response.ok) throw new Error('Review unavailable');
      const data = await response.json();
      const expiry = Date.parse(data.expiresAt);
      if (!Number.isFinite(expiry) || Date.now() > expiry) throw new Error('Review expired');
      return data;
    }
    if (!catalogPromise) catalogPromise = (async () => {
      const response = await fetch(config.editorial,{credentials:'same-origin'});
      if (!response.ok) throw new Error('Catalog unavailable');
      const data = await response.json();
      delete data.expiresAt;
      delete data.generatedAt;
      for (const photo of data.photos) {
        delete photo.labPilot;
        delete photo.labCatalog.status;
        delete photo.labCatalog.inventoryProtected;
        for (const key of ['status','orderable','reconciliationPending','inventoryProtected']) delete photo.artistEdition[key];
        for (const size of photo.artistEdition.sizes) for (const key of ['price','issuedCount','committedUnissuedCount','editionRemaining','nextEditionNumber']) delete size[key];
      }
      const active = await currentProducts();
      data.photos = data.photos.filter(photo => active.has(id(photo.labCatalog.productId)) || active.has(id(photo.artistEdition.productId))).map(photo => {
        const canonical = active.get(id(photo.labCatalog.productId)) || photo;
        const commercePaths = {lab:active.has(id(photo.labCatalog.productId)),artist:active.has(id(photo.artistEdition.productId))};
        return {...photo,title:canonical.title,description:canonical.description,image:canonical.image || photo.image,width:canonical.width || photo.width,height:canonical.height || photo.height,labCatalog:{...photo.labCatalog,handle:active.get(id(photo.labCatalog.productId))?.handle || photo.handle},commercePaths,commerce:{}};
      });
      for (const collection of data.collections) {
        collection.reviewUrl = root + 'collections/all?review_collection=' + encodeURIComponent(collection.handle);
        if (config.preview) collection.reviewUrl += '&preview_theme_id=' + config.themeId;
        if (config.preview) collection.reviewUrl += '&catalog=live';
      }
      return data;
    })().catch(error => { catalogPromise = null; throw error; });
    return catalogPromise;
  };
  const hydrate = async (photo, refresh = false) => {
    if (review) return;
    const [lab, artist] = await Promise.allSettled([
      photo.commercePaths?.lab === true ? product(photo.labCatalog.handle || photo.handle,photo.labCatalog.productId,refresh) : Promise.resolve(null),
      photo.commercePaths?.artist === true ? product(photo.artistEdition.handle,photo.artistEdition.productId,refresh) : Promise.resolve(null)
    ]);
    photo.commerce = {
      lab:lab.status === 'fulfilled' ? lab.value : null,
      artist:artist.status === 'fulfilled' ? artist.value : null
    };
  };
  const variant = (photo,kind,variantId) => {
    if (review || photo.commercePaths?.[kind] !== true) return null;
    const data = photo.commerce?.[kind];
    const allowed = kind === 'lab' ? photo.labCatalog?.variantIds : photo.artistEdition?.sizes.map(size=>size.variantId);
    if (!allowed?.some(value=>id(value) === id(variantId))) return null;
    return data?.variants.find(value=>id(value.id) === id(variantId)) || null;
  };
  const orderable = (photo,kind,variantId) => {
    const value = variant(photo,kind,variantId);
    return !!value && value.available === true && Number.isFinite(value.price) && value.price > 0;
  };
  const money = value => new Intl.NumberFormat(document.documentElement.lang || 'en',{style:'currency',currency:window.Shopify?.currency?.active || 'USD'}).format(value/100);
  window.PhotoCatalog = {enabled:config.enabled === true,review,preview:config.preview === true,labels:config.labels || {},load,hydrate,variant,orderable,money};
})();
